import { Market, MarketLanguageCodes } from '~/sharedInterfaces/SharedEnums';
import {
  IDropDownOption,
  ISliderValues,
  IAvailableMarkets,
} from '~/sharedInterfaces/SharedInterfaces';
import { getCurrency } from '~/utils';

interface IValueDefaults {
  min: number;
  max: number;
  step: number;
  defaultValue: number;
}

const DefaultValues: IAvailableMarkets<IValueDefaults> = {
  se: {
    min: 10000,
    max: 5000000,
    step: 10000,
    defaultValue: 100000,
  },
  no: {
    min: 10000,
    max: 5000000,
    step: 10000,
    defaultValue: 100000,
  },
  nl: {
    min: 1000,
    max: 500000,
    step: 1000,
    defaultValue: 10000,
  },
  fi: {
    min: 1000,
    max: 500000,
    step: 1000,
    defaultValue: 10000,
  },
  dk: {
    min: 10000,
    max: 2000000,
    step: 10000,
    defaultValue: 100000,
  },
  be: {
    min: 1000,
    max: 200000,
    step: 1000,
    defaultValue: 10000,
  },
};

export const getMaxValue = (market: keyof IAvailableMarkets<IValueDefaults>) => {
  return DefaultValues[market].max;
};

export const getMinValue = (market: keyof IAvailableMarkets<IValueDefaults>) => {
  return DefaultValues[market].min;
};

export const getStepValue = (market: keyof IAvailableMarkets<IValueDefaults>) => {
  return DefaultValues[market].step;
};

export const getDefaultValue = (market: keyof IAvailableMarkets<IValueDefaults>) => {
  return DefaultValues[market].defaultValue;
};

export const getDefaultValueOrMinValue = (
  market: keyof IAvailableMarkets<IValueDefaults>,
  inputValues: ISliderValues | undefined
) => {
  const defaultValue = inputValues?.defaultValue || getDefaultValue(market);
  const min = inputValues?.min || getMinValue(market);
  const returnValue = min > defaultValue ? min : defaultValue;
  return returnValue.toString();
};

const getDefaultLoanAmountValues = (market: keyof IAvailableMarkets<IValueDefaults>) => {
  const min = getMinValue(market);
  const max = getMaxValue(market);
  const step = getStepValue(market);
  const defaultValue = getDefaultValue(market);
  return { min, max, step, defaultValue };
};

export const getDefaultLoanAmountValuesOrInputValues = (
  market: keyof IAvailableMarkets<IValueDefaults>,
  inputValues: ISliderValues | undefined
) => {
  const min = inputValues?.min || getMinValue(market);
  const max = inputValues?.max || getMaxValue(market);
  const step = inputValues?.step || getStepValue(market);
  const defaultValue = inputValues?.defaultValue || getDefaultValue(market);
  const unit = getCurrency(market);
  return { min, max, step, defaultValue, unit };
};

const calculateStep = (
  step: number,
  mediumValues: number,
  largeValues: number,
  largeStep: number,
  mediumStep: number,
  i: number
) => {
  if (i >= largeValues) return largeStep;
  if (i >= mediumValues) return mediumStep;
  return step;
};

export const dropDownOptions = (market: keyof IAvailableMarkets<any>) => {
  const { min, max, step } = getDefaultLoanAmountValues(market);
  const mediumValues = min * 10;
  const largeValues = min * 50;
  const mediumStep = step * 5;
  const largeStep = step * 10;

  const options: IDropDownOption[] = [];
  for (
    let i = min;
    i <= max;
    i += calculateStep(step, mediumValues, largeValues, largeStep, mediumStep, i)
  ) {
    options.push({
      label: `${i} ${getCurrency(market)}`,
      value: i.toString(),
    });
  }

  return options;
};

const languageCode: IAvailableMarkets<MarketLanguageCodes> = {
  [Market.Sweden]: MarketLanguageCodes.Sweden,
  [Market.Denmark]: MarketLanguageCodes.Denmark,
  [Market.Netherlands]: MarketLanguageCodes.Netherlands,
  [Market.Finland]: MarketLanguageCodes.Finland,
  [Market.Belgium]: MarketLanguageCodes.Belgium,
  [Market.Norway]: MarketLanguageCodes.Norway,
};

export const formatSliderValue = (
  market: keyof IAvailableMarkets<IValueDefaults>,
  value: string,
  sliderValues: ISliderValues
) => {
  let currency;
  if (market === 'se') currency = 'SEK';
  else if (market === 'dk') currency = 'DKK';
  else if (market === 'no') currency = 'NOK';
  else currency = 'EUR';

  return {
    amount: `${new Intl.NumberFormat(languageCode[market], {
      currency,
      style: 'decimal',
    }).format(parseInt(value, 10))}`,
    unit: `${sliderValues.unit}`,
  };
};
